import { DocumentType, DOCUMENT_TYPE_OPTIONS } from 'src/app/pages/employees/enums/document-type.enum';
import { normalizeCountryValue } from './country.utils';

const ALL_DOCUMENT_TYPE_VALUES: DocumentType[] = DOCUMENT_TYPE_OPTIONS.map(option => option.value);

const COUNTRY_DOCUMENT_TYPES: Record<string, readonly DocumentType[]> = {
    ES: [DocumentType.WithoutDocument, DocumentType.DNI, DocumentType.NIE, DocumentType.Passport, DocumentType.CIF, DocumentType.Other],
    FR: [DocumentType.WithoutDocument, DocumentType.CNI, DocumentType.Passport, DocumentType.Other],
    GB: [DocumentType.WithoutDocument, DocumentType.NIN, DocumentType.Passport, DocumentType.Other],
    IT: [DocumentType.WithoutDocument, DocumentType.Passport, DocumentType.Other],
    PT: [DocumentType.WithoutDocument, DocumentType.PT_CARTAO, DocumentType.Passport, DocumentType.Other],
    DE: [DocumentType.WithoutDocument, DocumentType.Passport, DocumentType.Other],
    US: [DocumentType.WithoutDocument, DocumentType.Passport, DocumentType.Other],
    CL: [DocumentType.WithoutDocument, DocumentType.RUT, DocumentType.Passport, DocumentType.Other],
    CR: [DocumentType.WithoutDocument, DocumentType.Cedula, DocumentType.DIMEX, DocumentType.Passport, DocumentType.Other],
    MX: [DocumentType.WithoutDocument, DocumentType.INE, DocumentType.CURP, DocumentType.Passport, DocumentType.Other],
    TW: [DocumentType.WithoutDocument, DocumentType.Passport, DocumentType.TWID, DocumentType.TWARC, DocumentType.TWWDA, DocumentType.Other]
};

const COUNTRY_ALIASES: Record<string, keyof typeof COUNTRY_DOCUMENT_TYPES> = {
    ES: 'ES',
    ESPANA: 'ES',
    SPAIN: 'ES',

    FR: 'FR',
    FRANCIA: 'FR',
    FRANCE: 'FR',

    GB: 'GB',
    UK: 'GB',
    'UNITED KINGDOM': 'GB',
    'REINO UNIDO': 'GB',

    IT: 'IT',
    ITALIA: 'IT',
    ITALY: 'IT',

    PT: 'PT',
    PORTUGAL: 'PT',

    DE: 'DE',
    ALEMANIA: 'DE',
    GERMANY: 'DE',
    DEUTSCHLAND: 'DE',

    US: 'US',
    USA: 'US',
    'UNITED STATES': 'US',
    'ESTADOS UNIDOS': 'US',
    'EE UU': 'US',
    'EEUU': 'US',

    CL: 'CL',
    CHILE: 'CL',

    CR: 'CR',
    'COSTA RICA': 'CR',

    MX: 'MX',
    MEXICO: 'MX',

    TW: 'TW',
    TAIWAN: 'TW'
};

function normalizeCountryKey(value?: string | null): string {
    if (!value) return '';
    const normalized = normalizeCountryValue(value);
    return normalized
        .replace(/[\._\-\/]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function resolveCountryCode(country?: string | null): keyof typeof COUNTRY_DOCUMENT_TYPES | null {
    const key = normalizeCountryKey(country);
    if (!key) return null;

    if (COUNTRY_ALIASES[key]) {
        return COUNTRY_ALIASES[key];
    }

    if (key in COUNTRY_DOCUMENT_TYPES) {
        return key as keyof typeof COUNTRY_DOCUMENT_TYPES;
    }

    return null;
}

export function getDocumentTypesByCountry(
    country: string | null = null,
): DocumentType[] {
    const code = resolveCountryCode(country);

    if (code && COUNTRY_DOCUMENT_TYPES[code]) {
        return [...COUNTRY_DOCUMENT_TYPES[code]];
    }

    return [...ALL_DOCUMENT_TYPE_VALUES];
}

export function getDocumentTypeOptionsByCountry(
    country: string | null = null,
): typeof DOCUMENT_TYPE_OPTIONS {
    const allowed = getDocumentTypesByCountry(country);
    const allowedSet = new Set(allowed);

    return DOCUMENT_TYPE_OPTIONS.filter(option => allowedSet.has(option.value));
}
